import { BODY_CONTENT, CONTENT, FOOTER, HEADER } from '@opengov/stories-shared/constants';
import { IStoryContent, IStoryPadding } from '@opengov/stories-shared/types';
import { mapFontFamilyStrings } from '@opengov/stories-shared/utils/general';
import { filter, forEach, isNil } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import {
  ISettings,
  ISettingsColors,
  IStoryEditorContentSpacing,
  IStoryListQueryObject,
  IStoryVisibilityOptions
} from '../../types';
import { SECTION, TAG_DIV, TAG_SCRIPT } from '../constants';

export const addContentMedia = ({
  storyContentContent,
  media,
  mediaIndex = '0',
  mediaType = '',
  includeCommentMarkers = false
}: {
  storyContentContent: string;
  media: string;
  mediaIndex: string;
  mediaType: string;
  includeCommentMarkers: boolean;
}): string => {
  // Create a document fragment based on the storyContent content html string
  const fragment = document.createRange().createContextualFragment(storyContentContent);
  // Find the media block inside the document fragment
  const mediaBlocks: NodeListOf<Element> = fragment.querySelectorAll('.media-block');
  const mediaBlock: Element = mediaBlocks[mediaIndex] as Element;

  if (mediaBlock) {
    // Add class to block to show that it contains media
    mediaBlock.classList.add('has-media');

    // Add media index as a data attribute
    mediaBlock.setAttribute('data-media-index', (parseInt(mediaIndex, 10) + 1).toString());

    if (mediaBlock.hasAttribute('id') && mediaBlock.hasAttribute('data-media-index')) {
      mediaBlock.removeAttribute('id');
    }

    if (mediaType) {
      mediaBlock.setAttribute('data-media-type', mediaType);
    }
    // Create a temp element and inject the media content
    const temp = document.createElement('div');
    temp.innerHTML = media;
    // Add the media content into the media block
    const nodesToProcess = includeCommentMarkers ? temp.childNodes : temp.children;
    for (let i = 0; i < nodesToProcess.length; i++) {
      mediaBlock.appendChild(nodesToProcess[i].cloneNode(true));
    }
    const container =
      (fragment.firstElementChild as HTMLElement) ||
      (fragment.firstChild as HTMLElement) ||
      (fragment.children[0] as HTMLElement);
    if (container && container.outerHTML) {
      // Return new storyContent content
      return container.outerHTML;
    }
  }
  return '';
};

export const removeContentMedia = (storyContentContent: string, mediaIndex = 0): string => {
  // Create a document fragment based on the storyContent content html string
  const fragment = document.createRange().createContextualFragment(storyContentContent);
  // Find the media block inside the document fragment
  const mediaBlocks = fragment.querySelectorAll('.media-block');
  const mediaBlock = mediaBlocks[mediaIndex];
  if (mediaBlock) {
    // Remove all children from media block
    mediaBlock.innerHTML = '';
    // Remove has-media class
    mediaBlock.classList.remove('has-media');
    // Remove media type data attribute
    mediaBlock.removeAttribute('data-media-type');
    const container =
      (fragment.firstElementChild as HTMLElement) ||
      (fragment.firstChild as HTMLElement) ||
      (fragment.children[0] as HTMLElement);
    if (container && container.outerHTML) {
      // Return new storyContent content
      return container.outerHTML;
    }
  }
  return '';
};

export const addUuidToEditables = (storyContentContent: string): string => {
  const fragment = document.createRange().createContextualFragment(storyContentContent);
  const editableElements: HTMLDivElement[] = Array.from(fragment.querySelectorAll('.editable'));
  forEach(editableElements, (editableElement) => {
    editableElement.setAttribute('data-uuid', `${uuidv4()}`);
  });
  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  return container?.outerHTML ? container.outerHTML : '';
};

export const removeUuidFromEditables = (storyContentContent: string): string => {
  const fragment = document.createRange().createContextualFragment(storyContentContent);
  const editableElements: HTMLDivElement[] = Array.from(fragment.querySelectorAll('.editable'));
  forEach(editableElements, (editableElement) => {
    editableElement.removeAttribute('data-uuid');
  });
  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  return container?.outerHTML ? container.outerHTML : '';
};

export const updateEditableByUuid = (storyContentContent: string, uuid: string, value: string): string => {
  const fragment = document.createRange().createContextualFragment(storyContentContent);
  const editableElement = fragment.querySelector(`.editable[data-uuid="${uuid}"]`);
  if (editableElement) {
    editableElement.innerHTML = value;
  }
  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  return container?.outerHTML ? container.outerHTML : '';
};

export const clearStoryContent = (storyContentContent: string): string => {
  const fragment = document.createRange().createContextualFragment(storyContentContent);
  fragment.querySelectorAll('.editable').forEach((editableElement) => (editableElement.innerHTML = ''));
  fragment.querySelectorAll('.has-media').forEach((el) => {
    el.classList.remove('has-media');
    el.removeAttribute('data-embed-id');
    el.removeAttribute('data-selector');
    el.innerHTML = '';
  });
  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  // If node has background color
  container?.style.removeProperty('background-color');
  // If node has background image (header)
  container?.style.removeProperty('background-image');
  // If node has opacity (header-overlay)
  const headerOverlay = fragment.querySelector('.header-overlay') as HTMLElement;
  if (headerOverlay) {
    headerOverlay.style.opacity = '0.4';
  }
  return container?.outerHTML ? container.outerHTML : '';
};

function addContentSlide(storyContentContent: string): string {
  // Create a document fragment based on the storyContent content html string
  const fragment = document.createRange().createContextualFragment(storyContentContent);
  // Find the gallery wrapper and media blocks inside the document fragment
  const slickGallery = fragment.querySelector('.slick-gallery');
  const mediaBlocks = fragment.querySelectorAll('.media-block');
  // Create new slide from temp element
  const temp = document.createElement('div');
  temp.classList.add('gallery-block');
  temp.innerHTML = `<div data-media-index="${mediaBlocks.length + 1}" class="media-block slide-block"></div>`;
  // Append slide to gallery
  slickGallery.appendChild(temp);
  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  // This would suggest the HTML is 'corrupted' and needs manual cleanup via API
  if (!container) return '';
  // Return new storyContent content
  return container.outerHTML || '';
}

function removeContentSlide(storyContentContent: string, mediaIndex = 0): string {
  // Create a document fragment based on the storyContent content html string
  const fragment = document.createRange().createContextualFragment(storyContentContent);
  // Find the media block inside the document fragment
  const mediaBlocks = fragment.querySelectorAll('.media-block');
  const mediaBlock = mediaBlocks[mediaIndex];
  if (mediaBlock) {
    // Remove slide containing media block
    mediaBlock.parentNode.parentNode.removeChild(mediaBlock.parentNode);
    // Query updated media blocks
    const newMediaBlocks = fragment.querySelectorAll('.media-block');
    // Reassign 'data-media-index' to media blocks sequentially
    forEach(newMediaBlocks, (block, idx) => {
      if (block) {
        const mediaIndexAttr = block.getAttribute('data-media-index');
        if (mediaIndexAttr) {
          block.setAttribute('data-media-index', (idx + 1).toString());
        }
      }
    });
  }
  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  // This would suggest the HTML is 'corrupted' and needs manual cleanup via API
  if (!container) return '';
  // Return new storyContent content
  return container.outerHTML || '';
}

export const updateContentColor = (storyContentContent: string, color: string): string => {
  // Create a document fragment based on the storyContent content html string
  const fragment = document.createRange().createContextualFragment(storyContentContent);
  // Find the first (container) element
  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  // This would suggest the HTML is 'corrupted' and needs manual cleanup via API
  if (!container) return '';
  // Update the overlay element if it exists and container has background image
  const overlay = fragment.querySelector('.header-overlay') as HTMLElement;
  if (overlay && container.style.backgroundImage) {
    container.style.backgroundColor = '';
    overlay.style.backgroundColor = color;
    overlay.style.opacity = '0.4';
  } else {
    // Update background color of container
    container.style.backgroundColor = color;
    if (overlay) {
      overlay.style.backgroundColor = '';
      overlay.style.opacity = '0';
    }
  }
  // Return new storyContent content
  return container.outerHTML || '';
};

export const updateContentSpacing = (
  storyContentContent: string,
  { paddingTop, paddingBottom, breakBefore }: IStoryEditorContentSpacing
): string => {
  // Create a document fragment based on the storyContent content html string
  const fragment = document.createRange().createContextualFragment(storyContentContent);
  // Find the first (container) element
  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  // This would suggest the HTML is 'corrupted' and needs manual cleanup via API
  if (!container) return '';

  if (!isNil(paddingTop)) {
    container.style.paddingTop = `${paddingTop}px`;
    container.setAttribute('data-is-custom-padding-top', 'true');
  } else {
    container.style.paddingTop = '';
    container.removeAttribute('data-is-custom-padding-top');
  }

  if (!isNil(paddingBottom)) {
    container.style.paddingBottom = `${paddingBottom}px`;
    container.setAttribute('data-is-custom-padding-bottom', 'true');
  } else {
    container.style.paddingBottom = '';
    container.removeAttribute('data-is-custom-padding-bottom');
  }

  container.style.breakBefore = breakBefore ? 'page' : '';

  // Return new storyContent content
  return container.outerHTML || '';
};

function updateContentSpacingDefaults(
  storyContentContent: string,
  { paddingTop, paddingBottom }: IStoryPadding
): string {
  // Create a document fragment based on the storyContent content html string
  const fragment = document.createRange().createContextualFragment(storyContentContent);
  // Find the first (container) element
  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  // This would suggest the HTML is 'corrupted' and needs manual cleanup via API
  if (!container) return '';

  // Only update the style attributes if the node does not have a custom value
  if (!container.getAttribute('data-is-custom-padding-top')) {
    container.style.paddingTop = isNil(paddingTop) ? '' : `${paddingTop}px`;
  }

  if (!container.getAttribute('data-is-custom-padding-bottom')) {
    container.style.paddingBottom = isNil(paddingBottom) ? '' : `${paddingBottom}px`;
  }

  // Return new storyContent content
  return container.outerHTML || '';
}

export function clearInlineStyles(container: HTMLElement): HTMLElement {
  const children = container.querySelectorAll('*');
  forEach(children, (childElement) => {
    childElement.removeAttribute('style');
  });
  return container;
}

function updateContentFontColor(storyContentContent: string, color: string): string {
  // Create a document fragment based on the storyContent content html string
  const fragment = document.createRange().createContextualFragment(storyContentContent);
  // Find the first (container) element
  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  // This would suggest the HTML is 'corrupted' and needs manual cleanup via API
  if (!container) return '';
  const editableElements: HTMLDivElement[] = Array.from(container.querySelectorAll('.editable'));
  forEach(editableElements, (editableEl) => {
    clearInlineStyles(editableEl);
    editableEl.style.color = color;
  });
  // Return new storyContent content
  return container.outerHTML || '';
}

export interface IFonts {
  headline: string;
  paragraph: string;
}

export function updateContentFontFamily(storyContentContent: string, fonts: IFonts): string {
  // Create a document fragment based on the storyContent content html string
  const fragment = document.createRange().createContextualFragment(storyContentContent);
  // Find the first (container) element
  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  // This would suggest the HTML is 'corrupted' and needs manual cleanup via API
  if (!container) return '';

  const editableElements: HTMLDivElement[] = Array.from(container.querySelectorAll('.editable'));
  const mappedFontObj = mapFontFamilyStrings(fonts);
  forEach(editableElements, (editableEl) => {
    const paragraphs: HTMLParagraphElement[] = Array.from(editableEl.querySelectorAll('p'));
    const headers: HTMLHeadingElement[] = Array.from(editableEl.querySelectorAll('h1, h2, h3, h4, h5, h6'));
    const lists: (HTMLOListElement | HTMLUListElement)[] = Array.from(editableEl.querySelectorAll('ol, ul'));
    const paragraphsText: HTMLDivElement[] = Array.from(editableEl.querySelectorAll('.editable-paragraph-text'));

    if (editableEl.classList.contains('logotype')) {
      editableEl.style.fontFamily = `"${mappedFontObj.headline}"`;
    }

    forEach(headers, (headerEl) => {
      if (headerEl.parentNode) {
        (headerEl.parentNode as HTMLElement).style.fontFamily = `"${mappedFontObj.headline}"`;
      }
    });

    forEach(paragraphs, (paragraphEl) => {
      if (paragraphEl.parentNode) {
        (paragraphEl.parentNode as HTMLElement).style.fontFamily = `"${mappedFontObj.paragraph}"`;
      }
    });
    forEach(lists, (listEl) => {
      if (listEl.parentNode) {
        (listEl.parentNode as HTMLElement).style.fontFamily = `"${mappedFontObj.paragraph}"`;
      }
    });
    forEach(paragraphsText, (paragraphTextEl) => {
      if (paragraphTextEl.parentNode) {
        (paragraphTextEl.parentNode as HTMLElement).style.fontFamily = `"${mappedFontObj.paragraph}"`;
      }
    });
  });

  // Return new storyContent content
  return container.outerHTML || '';
}

function updateContentBackground(storyContentContent: string, imageUrl: string): string {
  // Create a document fragment based on the storyContent content html string
  const fragment = document.createRange().createContextualFragment(storyContentContent);
  // Find the first (container) element
  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  // This would suggest the HTML is 'corrupted' and needs manual cleanup via API
  if (!container) return '';
  // Update background image of container
  container.style.backgroundImage = `url('${imageUrl}')`;
  // Find the overlay element and update opacity
  const overlay = fragment.querySelector('.header-overlay') as HTMLElement;
  if (overlay) {
    overlay.style.opacity = '0.4';
  }
  // Return new storyContent content
  return container.outerHTML || '';
}

export function applyBrandingFontColorToContent(storyContentContent: string, colors: ISettingsColors): string {
  const { header, body } = colors;
  // Create a document fragment based on the storyContent content html string
  const fragment = document.createRange().createContextualFragment(storyContentContent);
  // Find the first (container) element
  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  // This would suggest the HTML is 'corrupted' and needs manual cleanup via API
  if (!container) return '';
  const editableElements: HTMLDivElement[] = Array.from(container.querySelectorAll('.editable'));
  let color = header.font.active;
  if (container.tagName === SECTION.toUpperCase()) {
    color = body.font.active;
  }
  forEach(editableElements, (editableEl) => {
    editableEl.style.color = color;
  });
  // Return new storyContent content
  return container.outerHTML || '';
}

function removeContentBackground(storyContentContent: string): string {
  // Create a document fragment based on the storyContent content html string
  const fragment = document.createRange().createContextualFragment(storyContentContent);
  // Find the first (container) element
  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  // This would suggest the HTML is 'corrupted' and needs manual cleanup via API
  if (!container) return '';
  // Update background image of container
  container.style.backgroundImage = null;
  // Find the overlay element and update opacity
  const overlay = fragment.querySelector('.header-overlay') as HTMLElement;
  if (overlay) {
    overlay.style.backgroundColor = '';
    overlay.style.opacity = '0';
  }
  // Return new storyContent content
  return container.outerHTML || '';
}

function addContentLogo(content: string, logoUrl: string): string {
  // Create a document fragment based on the storyContent content html string
  const fragment: DocumentFragment = document.createRange().createContextualFragment(content) as DocumentFragment;
  // Find the first (container) element
  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  // This would suggest the HTML is 'corrupted' and needs manual cleanup via API
  if (!container) return '';
  const logoImageWrapper = container.querySelector('.logo-image');
  const logoTextWrapper = container.querySelector('.logo');
  const img = document.createElement('img');

  // Show the logo image and hide the text
  logoTextWrapper.classList.add('hidden');
  logoImageWrapper.classList.remove('hidden');

  // Update background image of container
  img.setAttribute('src', logoUrl);
  // Add an empty alt tag since logos are decorative images
  // Make stories more accessible
  img.setAttribute('alt', 'Logo');
  logoImageWrapper.appendChild(img);

  // Return new storyContent content
  return container.outerHTML || '';
}

function removeContentLogo(content: string): string {
  // Create a document fragment based on the storyContent content html string
  const fragment = document.createRange().createContextualFragment(content);
  // Find the first (container) element
  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  // This would suggest the HTML is 'corrupted' and needs manual cleanup via API
  if (!container) return '';
  const logoImageWrapper = container.querySelector('.logo-image');
  const logoTextWrapper = container.querySelector('.logo');
  const img = logoImageWrapper.getElementsByTagName('img');

  // Remove all existing img elements
  for (let index = img.length - 1; index >= 0; index--) {
    img[index].parentNode.removeChild(img[index]);
  }

  // Show the text and hide the logo image
  logoTextWrapper.classList.remove('hidden');
  logoImageWrapper.classList.add('hidden');

  // Return new storyContent content
  return container.outerHTML || '';
}
/*
  This solution does not work on windows machine,
  it causes some unwanted behaviors that can only be solved by using Copy Plain Text option

  This method will remove the `editable` class from the HTML and put the contents inside a `span`.
  It is used by the handlePaste method, to prevent creating a text box inside a text box on pasting
*/
export function processHtml(content: string): string {
  const fragment = document.createRange().createContextualFragment(content);
  const div = document.createElement('div');
  for (let index = 0; index < fragment.childNodes.length; index++) {
    const container = fragment.children[index] as HTMLElement;
    const newEl = document.createElement('span');
    if (!container || typeof container.classList === 'undefined' || typeof container.querySelector !== 'function') {
      continue;
    }
    if (container.classList.contains('editable')) {
      newEl.innerHTML = container.innerHTML;
      div.appendChild(newEl);
    } else if (container.classList.contains('col-2')) {
      const childContainer = container.children[0];
      if (
        !childContainer ||
        typeof childContainer.classList === 'undefined' ||
        typeof childContainer.querySelector !== 'function'
      ) {
        continue;
      }
      if (childContainer.classList.contains('editable')) {
        newEl.innerHTML = childContainer.innerHTML;
        div.appendChild(newEl);
      }
    } else if (container.classList.contains('col-3')) {
      const childContainer = container.children[0];
      if (
        !childContainer ||
        typeof childContainer.classList === 'undefined' ||
        typeof childContainer.querySelector !== 'function'
      ) {
        continue;
      }
      if (childContainer.classList.contains('editable')) {
        newEl.innerHTML = childContainer.innerHTML;
        div.appendChild(newEl);
      }
    }
  }
  if (div.innerHTML === '') {
    return content;
  }
  return div.innerHTML;
}

function copyStoryContentContent(storyContentContent: string, newStoryContentContent: string): string {
  // Create document fragments based on the storyContent content html strings
  const fragment1 = document.createRange().createContextualFragment(storyContentContent);
  const fragment2 = document.createRange().createContextualFragment(newStoryContentContent);
  // Find the first (container) elements
  const container1: HTMLElement =
    (fragment1.firstElementChild as HTMLElement) ||
    (fragment1.firstChild as HTMLElement) ||
    (fragment1.children[0] as HTMLElement);
  const container2: HTMLElement =
    (fragment2.firstElementChild as HTMLElement) ||
    (fragment2.firstChild as HTMLElement) ||
    (fragment2.children[0] as HTMLElement);
  // This would suggest the HTML is 'corrupted' and needs manual cleanup via API
  if (!container1 || !container2) return '';
  // Find column or full-width element groups
  const container1columnsList =
    container1.querySelectorAll('[class*="col-"]').length !== 0
      ? container1.querySelectorAll('[class*="col-"]')
      : container1.childNodes;
  const container1columns: Element[] = Array.prototype.slice.call(container1columnsList);
  const container2columnsList =
    container2.querySelectorAll('[class*="col-"]').length !== 0
      ? container2.querySelectorAll('[class*="col-"]')
      : container2.childNodes;
  const container2columns: Element[] = Array.prototype.slice.call(container2columnsList);
  // Update new storyContent container styles
  if (container1.style && container1.style.cssText) {
    container2.style.cssText = container1.style.cssText;
  }
  // Handle content copying
  if (container1.classList.contains('header')) {
    // Handle background image
    if (container1.style.backgroundImage) {
      container2.style.backgroundImage = container1.style.backgroundImage;
    }

    // Handle logo
    if (container1.querySelector('.logo') && container2.querySelector('.logo')) {
      container2.querySelector('.logo').innerHTML = container1.querySelector('.logo').innerHTML;
    }
    // Handle logo image
    if (container1.querySelector('.logo-image') && container2.querySelector('.logo-image')) {
      container2.querySelector('.logo-image').innerHTML = container1.querySelector('.logo-image').innerHTML;
    }
    // Handle display toggle of custom logo
    if (container1.querySelector('.logo').classList.contains('hidden')) {
      container2.querySelector('.logo').classList.add('hidden');
      container2.querySelector('.logo-image').classList.remove('hidden');
    }
    // Handle links
    if (container1.querySelector('.links') && container2.querySelector('.links')) {
      container2.querySelector('.links').innerHTML = container1.querySelector('.links').innerHTML;
    }
    // Handle content
    if (container1.querySelector('.header-content') && container2.querySelector('.header-content')) {
      const container1ContentChildren = filter(
        container1.querySelector('.header-content').children,
        (child) => child && typeof child.classList !== 'undefined' && child.classList.contains('editable')
      );
      const container2ContentChildren = filter(
        container2.querySelector('.header-content').children,
        (child) => child && typeof child.classList !== 'undefined' && child.classList.contains('editable')
      );
      // Loop over each set of editable children and copy content if tagName match is found (h2 -> h2, p -> p, ect)
      forEach(container1ContentChildren, (container1Child) => {
        forEach(container2ContentChildren, (container2Child) => {
          const container1FirstChild =
            (container1Child.firstElementChild as HTMLElement) ||
            (container1Child.firstChild as HTMLElement) ||
            (container1Child.children[0] as HTMLElement);
          const container2FirstChild =
            (container2Child.firstElementChild as HTMLElement) ||
            (container2Child.firstChild as HTMLElement) ||
            (container2Child.children[0] as HTMLElement);
          if (container1FirstChild.tagName === container2FirstChild.tagName) {
            container2Child.innerHTML = container1Child.innerHTML;
          }
        });
      });
    }
    // Handle overlay
    if (container1.querySelector('.header-overlay') && container2.querySelector('.header-overlay')) {
      const container1HeaderOverlay = container1.querySelector('.header-overlay') as HTMLElement;
      const container2HeaderOverlay = container2.querySelector('.header-overlay') as HTMLElement;
      container2HeaderOverlay.style.cssText = container1HeaderOverlay.style.cssText;
    }
  } else {
    let mediaBlocksContainer1: Element[] = [];
    let contentBlocksContainer1: Element[] = [];
    let mediaBlocksContainer2: Element[] = [];
    let contentBlocksContainer2: Element[] = [];

    function createContentAndMediaBlocks(columns: Element[]) {
      const contentBlocks: Element[] = [];
      const mediaBlocks: Element[] = [];

      forEach(columns, (column) => {
        // Skip if column is not an Element (e.g., text nodes, comment nodes)
        if (!column || typeof column.classList === 'undefined' || typeof column.querySelector !== 'function') {
          return;
        }

        // Array.prototype.slice.call appends to the same object. Don't change contentBlock to a const.
        let contentBlock: Element[];
        if (column.classList.contains('full-width-content')) {
          contentBlock = Array.prototype.slice.call(column.childNodes);
        } else if (column.querySelector('[class*="-content"]')) {
          contentBlock = Array.prototype.slice.call(column.querySelector('[class*="-content"]').childNodes);
        } else if (column.querySelector('.editable')) {
          contentBlock = [column.querySelector('.editable')];
        } else {
          contentBlock = [];
        }

        if (contentBlock) {
          contentBlocks.push(...contentBlock);
        }
        const mediaBlock = column.classList.contains('media-block') ? column : column.querySelector('.media-block');
        if (mediaBlock) {
          mediaBlocks.push(mediaBlock);
        }
      });

      return [contentBlocks, mediaBlocks];
    }

    [contentBlocksContainer1, mediaBlocksContainer1] = createContentAndMediaBlocks(container1columns);
    [contentBlocksContainer2, mediaBlocksContainer2] = createContentAndMediaBlocks(container2columns);

    // Handle media block
    if (mediaBlocksContainer1.length !== 0 && mediaBlocksContainer2.length !== 0) {
      forEach(mediaBlocksContainer1, (container, idx) => {
        if (container.classList.contains('has-media')) {
          mediaBlocksContainer2[idx].classList.add('has-media');
          mediaBlocksContainer2[idx].setAttribute('data-media-type', container.getAttribute('data-media-type'));
          mediaBlocksContainer2[idx].setAttribute('data-media-index', container.getAttribute('data-media-index'));
          mediaBlocksContainer2[idx].innerHTML = container.innerHTML;
        }
      });
    }
    contentBlocksContainer1 = filter(
      contentBlocksContainer1,
      (container) => container && typeof container.classList !== 'undefined'
    );
    contentBlocksContainer2 = filter(
      contentBlocksContainer2,
      (container) => container && typeof container.classList !== 'undefined'
    );
    //Handle text block (.editable and .content)
    if (contentBlocksContainer1.length !== 0 && contentBlocksContainer2.length !== 0) {
      if (container1.classList.contains('full-width')) {
        let i: number;
        let j: number;

        for (i = 0; i < contentBlocksContainer1.length; i++) {
          for (j = 0; j < contentBlocksContainer2.length; j++) {
            if (contentBlocksContainer1[i].classList.value === contentBlocksContainer2[j].classList.value) {
              contentBlocksContainer2[j].innerHTML = contentBlocksContainer1[i].innerHTML;
            }
          }
        }
      } else {
        forEach(contentBlocksContainer1, (container1Child, idx) => {
          const container2FirstChild = contentBlocksContainer2[idx];
          if (container2FirstChild && container1Child) {
            container2FirstChild.innerHTML = container1Child.innerHTML;
          }
        });
      }
    }
  }
  // Return new storyContent content
  return container2.outerHTML || '';
}

function updateStoryContentElementId(content: string, storyContentId: string): string {
  // Create a document fragment based on the storyContent content html string
  const fragment = document.createRange().createContextualFragment(content);
  // Find the first (container) element
  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  // This would suggest the HTML is 'corrupted' and needs manual cleanup via API
  if (!container) return '';
  // Update background image of container
  container.id = `storyContent-${storyContentId}`;
  // Return new storyContent content
  return container.outerHTML || '';
}

export function addMedia(
  storyContent: IStoryContent,
  media: string,
  mediaIndex: number,
  mediaType: string,
  includeCommentMarkers = false
): IStoryContent {
  return {
    ...storyContent,
    shouldUpdate: true,
    content: addContentMedia({
      storyContentContent: storyContent.content,
      media,
      mediaIndex: String(mediaIndex),
      mediaType,
      includeCommentMarkers
    })
  };
}

export function removeMedia(storyContent: IStoryContent, mediaIndex: number): IStoryContent {
  return {
    ...storyContent,
    shouldUpdate: true,
    content: removeContentMedia(storyContent.content, mediaIndex)
  };
}

export function addSlide(storyContent: IStoryContent): IStoryContent {
  return {
    ...storyContent,
    shouldUpdate: true,
    content: addContentSlide(storyContent.content)
  };
}

export function removeSlide(storyContent: IStoryContent, mediaIndex: number): IStoryContent {
  return {
    ...storyContent,
    shouldUpdate: true,
    content: removeContentSlide(storyContent.content, mediaIndex)
  };
}

export function updateSpacing(storyContent: IStoryContent, spacing: IStoryEditorContentSpacing): IStoryContent {
  return {
    ...storyContent,
    shouldUpdate: true,
    content: updateContentSpacing(storyContent.content, spacing)
  };
}

export function updateSpacingDefaults(storyContent: IStoryContent, storyPadding: IStoryPadding): IStoryContent {
  return {
    ...storyContent,
    shouldUpdate: true,
    content: updateContentSpacingDefaults(storyContent.content, storyPadding)
  };
}

export function updateColor(storyContent: IStoryContent, color: string): IStoryContent {
  return {
    ...storyContent,
    shouldUpdate: true,
    content: updateContentColor(storyContent.content, color)
  };
}

export function updateFontColor(storyContent: IStoryContent, color: string): IStoryContent {
  return {
    ...storyContent,
    shouldUpdate: true,
    content: updateContentFontColor(storyContent.content, color)
  };
}

export function updateFontFamily(storyContent: IStoryContent, fonts: IFonts): IStoryContent {
  return {
    ...storyContent,
    shouldUpdate: true,
    content: updateContentFontFamily(storyContent.content, fonts)
  };
}

export function addBackground(storyContent: IStoryContent, imageUrl: string): IStoryContent {
  return {
    ...storyContent,
    shouldUpdate: true,
    content: updateContentBackground(storyContent.content, imageUrl)
  };
}

export function removeBackground(storyContent: IStoryContent): IStoryContent {
  return {
    ...storyContent,
    shouldUpdate: true,
    content: removeContentBackground(storyContent.content)
  };
}

export function copyContent(storyContent: IStoryContent, newStoryContent: IStoryContent) {
  return copyStoryContentContent(storyContent.content, newStoryContent.content);
}

export function updateLogo(storyContent, text, logoUrl?) {
  // If a logoUrl is passed, replace default header text with logo image
  if (logoUrl) {
    return {
      ...storyContent,
      shouldUpdate: true,
      content: addContentLogo(storyContent.content, logoUrl)
    };
  } else if (storyContent.content.indexOf('LOGOTYPE') > -1) {
    // Replace default header text (LOGOTYPE) with new text if it exists
    return {
      ...storyContent,
      shouldUpdate: true,
      content: storyContent.content.replace('LOGOTYPE', text)
    };
  }

  return storyContent;
}

export function removeLogo(storyContent: IStoryContent): IStoryContent {
  return {
    ...storyContent,
    shouldUpdate: true,
    content: removeContentLogo(storyContent.content)
  };
}
export function constructGetStoryVisibilityParams(
  queryObject: IStoryListQueryObject,
  storyVisibility: IStoryVisibilityOptions
): IStoryListQueryObject {
  let queryObjectResult;
  if (storyVisibility) {
    queryObjectResult = {
      ...queryObject,
      state: storyVisibility
    };
    if (storyVisibility === 'all') {
      delete queryObjectResult.state;
    }
  }
  return queryObjectResult;
}
export function applyBrandingSettings(
  storyContent: IStoryContent,
  settings: ISettings,
  entityName: string
): IStoryContent {
  const { header, body, footer } = settings.brand.colors;
  const { logo } = settings.brand;
  let headerLogo = logo.header;
  let footerLogo = logo.footer;
  let fonts;
  let newStoryContent = storyContent;

  // Handle logos
  if (logo.placement.header_as_footer) footerLogo = headerLogo;
  if (logo.placement.header_only) footerLogo = '';
  if (logo.placement.footer_only) {
    footerLogo = footerLogo || headerLogo;
    headerLogo = '';
  }

  // Handle fonts
  if (settings && settings.brand) {
    fonts = settings.brand.fonts;
  }

  if (fonts.headline || fonts.paragraph) {
    newStoryContent = updateFontFamily(newStoryContent, fonts);
  }

  switch (storyContent.type) {
    case HEADER:
      newStoryContent = updateColor(newStoryContent, header.background.active);
      newStoryContent = updateFontColor(newStoryContent, header.font.active);
      newStoryContent = removeLogo(newStoryContent);
      if (headerLogo) {
        newStoryContent = updateLogo(newStoryContent, entityName.toUpperCase(), headerLogo);
      }
      break;
    case CONTENT:
    case BODY_CONTENT:
      newStoryContent = updateColor(newStoryContent, body.background.active);
      newStoryContent = updateFontColor(newStoryContent, body.font.active);
      break;
    case FOOTER:
      newStoryContent = updateColor(newStoryContent, footer.background.active);
      newStoryContent = updateFontColor(newStoryContent, footer.font.active);
      newStoryContent = removeLogo(newStoryContent);
      if (footerLogo) {
        newStoryContent = updateLogo(newStoryContent, entityName.toUpperCase(), footerLogo);
      }
    default:
      return newStoryContent;
  }

  return newStoryContent;
}

export function updateStoryContentId(storyContent: IStoryContent): IStoryContent {
  if (
    storyContent &&
    storyContent._id &&
    storyContent.content &&
    storyContent.content.indexOf(`storyContent-${storyContent._id}`) > -1
  ) {
    return storyContent;
  }

  return {
    ...storyContent,
    content: updateStoryContentElementId(storyContent.content, storyContent._id)
  };
}

export function htmlDecode(input: string): string {
  const e = document.createElement('div');
  e.innerHTML = input;
  // Handle case of empty input
  return e.childNodes.length === 0 ? '' : e.childNodes[0].nodeValue;
}

// Takes a style attribute string and returns an object with camelcased rules as key/value
export function getStyles(style: string): Partial<CSSStyleDeclaration> {
  const output = {};

  if (!style) {
    return output;
  }

  const camelize = (text) => {
    return text.replace(/(?:^|[-])(\w)/g, (a, c) => {
      c = a.substr(0, 1) === '-' ? c.toUpperCase() : c;
      return c ? c : '';
    });
  };

  // some safety around inline styles to avoid issues with
  // incomplete style strings, like this: "color: black; font-family: "
  if (style.trim().slice(-1) === ':') {
    style = `${style} initial`;
  }

  const str = style.split(';');

  for (let i = 0; i < str.length; ++i) {
    if (str[i]) {
      const rule = str[i].trim();

      if (rule) {
        const ruleParts = rule.split(/:(.+)/);
        const key = camelize(ruleParts[0].trim());
        output[key] = ruleParts[1].trim();
      }
    }
  }

  return output;
}

export function updateContentTileMedia(
  storyContent: IStoryContent,
  mediaToAdd,
  totalMediaBlocks: number,
  embedMediaType: string,
  currentBlock?: number
): IStoryContent {
  let current = currentBlock || 1;
  const mediaType = embedMediaType;
  const mediaIndex = current - 1;
  const media = mediaToAdd[mediaIndex];

  // Remove media from storyContent
  const updatedStoryContent = removeMedia(storyContent, media.mediaIndex);
  // Add div media to storyContent
  const newStoryContent = addMedia(updatedStoryContent, media.mediaDiv, media.mediaIndex, mediaType);
  // Add script media to storyContent
  const finalStoryContent = addMedia(newStoryContent, media.mediaScript, media.mediaIndex, mediaType);

  current += 1;

  if (current <= totalMediaBlocks) {
    return updateContentTileMedia(finalStoryContent, mediaToAdd, totalMediaBlocks, mediaType, current);
  } else {
    return finalStoryContent;
  }
}

export function getEmbedMediaBlockAttributes(mediaBlock: Element): {
  embedId: string;
  divId: string;
  updateSetting: string;
  disableClick: boolean;
  showReportLink: boolean;
  embedTitle: string;
  displayAccountCodes: boolean;
  embedVersion: string;
} {
  let embedId = '';
  let divId = '';
  let updateSetting = 'snapshot';
  let disableClick = false;
  let showReportLink = true;
  let displayAccountCodes = false;
  let embedTitle = '';
  let embedVersion = '';
  for (let i = 0; i < mediaBlock.children.length; i++) {
    if (mediaBlock.children[i].tagName === TAG_SCRIPT && mediaBlock.children[i].hasAttribute('data-embed-id')) {
      embedId = mediaBlock.children[i].getAttribute('data-embed-id');
    }
    if (mediaBlock.children[i].tagName === TAG_DIV && mediaBlock.children[i].hasAttribute('id')) {
      divId = mediaBlock.children[i].getAttribute('id');
    }
    if (mediaBlock.children[i].tagName === TAG_DIV && mediaBlock.children[i].hasAttribute('data-update-setting')) {
      updateSetting = mediaBlock.children[i].getAttribute('data-update-setting');
    }
    if (mediaBlock.children[i].tagName === TAG_DIV && mediaBlock.children[i].hasAttribute('data-embed-disable-click')) {
      disableClick = mediaBlock.children[i].getAttribute('data-embed-disable-click') === 'true';
    }
    if (
      mediaBlock.children[i].tagName === TAG_DIV &&
      mediaBlock.children[i].hasAttribute('data-embed-show-report-link')
    ) {
      showReportLink = mediaBlock.children[i].getAttribute('data-embed-show-report-link') === 'true';
    }
    if (
      mediaBlock.children[i].tagName === TAG_DIV &&
      mediaBlock.children[i].hasAttribute('data-embed-display-account-codes')
    ) {
      displayAccountCodes = mediaBlock.children[i].getAttribute('data-embed-display-account-codes') === 'true';
    }
    if (mediaBlock.children[i].tagName === TAG_DIV && mediaBlock.children[i].hasAttribute('data-embed-title')) {
      embedTitle = mediaBlock.children[i].getAttribute('data-embed-title');
    }
    if (mediaBlock.children[i].tagName === TAG_DIV && mediaBlock.children[i].hasAttribute('data-embed-version')) {
      embedVersion = mediaBlock.children[i].getAttribute('data-embed-version');
    }
  }
  return { embedId, divId, updateSetting, disableClick, showReportLink, embedTitle, displayAccountCodes, embedVersion };
}

export function removeHiddenClass(content: string, mediaIndex: number): string {
  return content.replace(`hidden-${mediaIndex}`, '');
}

const extactEmbedsFromMediaBlock = (mediaBlocks: NodeListOf<Element>): string[] => {
  const embeds: string[] = [];
  mediaBlocks.forEach((mediaBlock) => {
    for (let i = 0; i < mediaBlock.children.length; i++) {
      const embedId = mediaBlock.children[i].getAttribute('data-embed-id');
      if (!embeds.includes(embedId)) embeds.push(embedId);
    }
  });
  return embeds;
};

export const storyEmbedsToRemove = (storyContent: string, newContent: string): string[] => {
  const oldContentFragment = document.createRange().createContextualFragment(storyContent);
  const newContentFragment = document.createRange().createContextualFragment(newContent);
  const mediaBlocksFromOldContent = oldContentFragment.querySelectorAll('.media-block');
  const mediaBlocksFromNewContent = newContentFragment.querySelectorAll('.media-block');
  const embedsFromOldContent: string[] = extactEmbedsFromMediaBlock(mediaBlocksFromOldContent);
  const embedsFromNewContent: string[] = extactEmbedsFromMediaBlock(mediaBlocksFromNewContent);

  const embedsToRemove = embedsFromOldContent.filter((embedId) => !embedsFromNewContent.includes(embedId));
  return embedsToRemove;
};

export function wrapHtmlWithTitleAndSpreadsheetCommentMarkers({
  content,
  title
}: {
  content: string;
  title: string;
}): string {
  return `
    <div class="custom-spreadsheet-report-container" data-custom-spreadsheet-report-element="container">
      <h3 class="custom-spreadsheet-report-title" data-custom-spreadsheet-report-element="title">${title}</h3>
      <!-- custom-spreadsheet-table-markup-start -->
      ${content}
      <!-- custom-spreadsheet-table-markup-end -->
    </div>`;
}

export function replaceTitleInSpreadsheetTableMarkup({
  storyContentContent,
  mediaIndex,
  newTitle
}: {
  storyContentContent: string;
  mediaIndex: number;
  newTitle: string;
}) {
  const fragment = document.createRange().createContextualFragment(storyContentContent);
  // Find the media block inside the document fragment
  const mediaBlocks = fragment.querySelectorAll('.media-block');
  const mediaBlock = mediaBlocks[mediaIndex];

  if (mediaBlock) {
    // Find the title element within the media block
    const titleElement = mediaBlock.querySelector('[data-custom-spreadsheet-report-element="title"]');
    if (titleElement) {
      // Replace the title text content
      titleElement.textContent = newTitle;
    }

    const container =
      (fragment.firstElementChild as HTMLElement) ||
      (fragment.firstChild as HTMLElement) ||
      (fragment.children[0] as HTMLElement);

    if (container && container.outerHTML) {
      // Return new storyContent content
      return container.outerHTML;
    }
  }

  return '';
}
